import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, Repeat, Clock, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, setOrgToken, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLocale } from "../lib/locale";
import { pricingPillLabel } from "../lib/format";
import { resolveEffectivePricing, earlyBirdLeadMs, earlyBirdRecurringFormHint } from "../lib/pricing";
import { eventPublicPath } from "../lib/eventLinks";
import { ReadableCard, ReadableNavButton } from "../components/Sticker";
import FormLabel from "../components/FormLabel";
import FormFieldError, { fieldClass } from "../components/FormFieldError";
import FormTextField from "../components/FormTextField";
import InstagramInput from "../components/InstagramInput";
import AuthModal from "../components/AuthModal";
import CreateEventStickyBar, { getCreateEventPublishLabel } from "../components/CreateEventStickyBar";
import SelfPaymentForm from "../components/SelfPaymentForm";
import CityAutocomplete from "../components/CityAutocomplete";
import VenueInput from "../components/VenueInput";
import CreateEventSuccessScreen from "../components/createEvent/CreateEventSuccessScreen";
import CreateEventLoadingSkeleton from "../components/createEvent/CreateEventLoadingSkeleton";
import NotifyAfterSaveDialog from "../components/broadcasts/NotifyAfterSaveDialog";
import {
  ChoiceToggleGroup,
  FieldHint,
  FORM_CARD_CLASS,
  FORM_STYLE_CARD_CLASS,
  FormValidationAlert,
  SectionTitle,
} from "../components/createEvent/CreateEventFormUi";
import CoverColorPicker from "../components/createEvent/CoverColorPicker";
import {
  CREATE_EVENT_FORM_ID,
  CREATE_EVENT_FIELD_IDS,
  CREATE_EVENT_CO_HOST_FIELD_ID,
  EMPTY_FORM,
  coHostCandidateGuests,
  coHostOptionLabel,
  coHostsPayloadFromForm,
  isMaybeCoHostGuest,
  createEventFormNeedsLeaveConfirm,
  eventToForm,
  pricingForcesInviteOnly,
  resolveCoHostFromGuests,
  scrollToFirstFieldError,
  validateCreateEventForm,
} from "../lib/createEventForm";
import { emptyPaymentFields } from "../lib/payment";
import { normalizeInstagramUrl } from "../lib/instagram";
import { promoteEventCoAdmin } from "../lib/schools";
import HostSchoolPromotionField from "../components/schools/HostSchoolPromotionField";
import ConfirmLeaveModal from "../components/ConfirmLeaveModal";
import { normalizeExternalSchoolUrl } from "../lib/hostSchool";

function buildHostSchoolPayload(form) {
  if (form.host_school_opt_out) {
    return {
      host_school_opt_out: true,
      school_id: null,
      school_external_name: null,
      school_external_url: null,
    };
  }
  const externalName = String(form.school_external_name || "").trim() || null;
  const externalUrl = normalizeExternalSchoolUrl(form.school_external_url);
  if (form.school_id) {
    return {
      host_school_opt_out: false,
      school_id: form.school_id,
      school_external_name: null,
      school_external_url: null,
    };
  }
  if (externalName || form.school_external_url) {
    return {
      host_school_opt_out: false,
      school_id: null,
      school_external_name: externalName,
      school_external_url: externalUrl,
    };
  }
  return {
    host_school_opt_out: false,
    school_id: null,
    school_external_name: null,
    school_external_url: null,
  };
}

export default function CreateEventPage() {
  const { eventId } = useParams();
  const isEdit = Boolean(eventId);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const fromOnboarding = !isEdit && searchParams.get("onboarding") === "1";
  const { account, checking, refresh } = useAuth();
  const locale = useLocale();
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [sourceEvent, setSourceEvent] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [notifyAfterSaveOpen, setNotifyAfterSaveOpen] = useState(false);
  const [pendingNotifyEvent, setPendingNotifyEvent] = useState(null);
  const [pendingLeaveTo, setPendingLeaveTo] = useState(null);
  const [leaveRedirect, setLeaveRedirect] = useState(null);
  const [coHostGuests, setCoHostGuests] = useState([]);
  const [loadingCoHostGuests, setLoadingCoHostGuests] = useState(false);
  const [coHostGuestsError, setCoHostGuestsError] = useState(null);
  const [coHostGuestsReloadToken, setCoHostGuestsReloadToken] = useState(0);

  const isDraft = sourceEvent?.lifecycle === "draft";

  const [form, setForm] = useState({ ...EMPTY_FORM, self_payment: { use_profile_defaults: true, ...emptyPaymentFields(null) } });

  const previewPricing = useMemo(() => {
    if (form.pricing_type === "free") return { type: "free" };
    const base = {
      type: form.pricing_type,
      amount: form.pricing_amount ? parseFloat(form.pricing_amount) : null,
      currency: form.pricing_currency || "EUR",
    };
    if (!form.early_bird_enabled || !form.early_bird_amount) return base;
    const leadMs =
      form.recurring !== "none" && form.starts_at && form.early_bird_cutoff
        ? earlyBirdLeadMs(form.starts_at, form.early_bird_cutoff)
        : null;
    return resolveEffectivePricing(
      {
        ...base,
        early_bird_amount: parseFloat(form.early_bird_amount),
        early_bird_cutoff: form.early_bird_cutoff ? new Date(form.early_bird_cutoff).toISOString() : null,
        early_bird_max_rsvps: form.early_bird_max_rsvps ? parseInt(form.early_bird_max_rsvps, 10) : null,
        early_bird_lead_ms: leadMs,
      },
      {
        occurrenceStartsAt:
          form.recurring !== "none" && form.starts_at
            ? new Date(form.starts_at).toISOString()
            : null,
      },
    );
  }, [form]);

  const previewPaymentPricing = useMemo(() => {
    if (!previewPricing || previewPricing.type === "free") return previewPricing;
    const amt = previewPricing.effective_amount ?? previewPricing.amount;
    return { ...previewPricing, amount: amt };
  }, [previewPricing]);

  const earlyBirdRecurringHint = useMemo(
    () =>
      earlyBirdRecurringFormHint({
        recurring: form.recurring,
        starts_at: form.starts_at,
        early_bird_cutoff: form.early_bird_cutoff,
        early_bird_enabled: form.early_bird_enabled,
      }),
    [form.recurring, form.starts_at, form.early_bird_cutoff, form.early_bird_enabled],
  );

  const selectedCoHostId = form.co_hosts?.[0]?.dancer_id || "";
  const selectedCoHostGuest = selectedCoHostId
    ? coHostGuests.find((g) => g.dancer_id === selectedCoHostId)
    : null;
  const selectedCoHostIsMaybe = isMaybeCoHostGuest(selectedCoHostGuest);

  // Apply locale defaults once (currency selection; city stays empty unless account profile has one)
  useEffect(() => {
    if (locale.loading) return;
    setForm((f) => ({
      ...f,
      pricing_currency:
        f.pricing_currency === "EUR" && locale.currency && locale.currency !== "EUR"
          ? locale.currency
          : f.pricing_currency,
    }));
  }, [locale.loading, locale.currency]);

  // Keep host profile payment settings fresh (e.g. after saving on /me/host).
  useEffect(() => {
    if (!isEdit) refresh();
  }, [isEdit, refresh]);

  // Prefill from account profile (create only)
  useEffect(() => {
    if (isEdit || !account) return;
      const paymentDefaults = emptyPaymentFields(account);
      setForm((f) => ({
        ...f,
        organizer_name: f.organizer_name || account.name || "",
        city: f.city || account.base_city || account.school_city || (!locale.loading ? locale.default_city : "") || "",
        city_location: f.city_location || account.base_city_location || account.school_city_location || null,
        self_payment:
          f.self_payment?.use_profile_defaults !== false
            ? { use_profile_defaults: true, ...paymentDefaults }
            : f.self_payment?.payment_link || f.self_payment?.payment_upi
              ? f.self_payment
              : { use_profile_defaults: false, ...paymentDefaults },
      }));
  }, [account, isEdit, locale.loading, locale.default_city]);

  useEffect(() => {
    if (isEdit || checking) return;
    if (!account) {
      toast.error("Sign in to publish your event.");
      nav("/login?next=/create", { replace: true });
    }
  }, [isEdit, account, checking, nav]);

  useEffect(() => {
    if (!isEdit || checking) return;
    if (!account) {
      nav(`/login?next=/e/${eventId}/edit`);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/events/${eventId}`, { params: { for_edit: "1" } });
        if (cancelled) return;
        if (!r.data.is_hosting) {
          setLoadError("forbidden");
          return;
        }
        setForm(eventToForm(r.data));
        setSourceEvent(r.data);
      } catch {
        if (!cancelled) setLoadError("not_found");
      } finally {
        if (!cancelled) setLoadingEvent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, eventId, account, checking, nav]);

  // Load RSVP'd guests for co-host picker after the event form is loaded.
  const sourceEventId = sourceEvent?.id || null;
  const sourceOrganizerName = sourceEvent?.organizer_name || "";
  useEffect(() => {
    if (!isEdit || !eventId || !account || loadError || !sourceEventId) {
      if (!isEdit) {
        setCoHostGuests([]);
        setCoHostGuestsError(null);
        setLoadingCoHostGuests(false);
      }
      return;
    }
    let cancelled = false;
    setLoadingCoHostGuests(true);
    setCoHostGuestsError(null);
    (async () => {
      try {
        const r = await api.get(`/events/${eventId}/dashboard`);
        if (cancelled) return;
        const hostNames = [account?.name, sourceOrganizerName, r.data?.organizer_name];
        const candidates = coHostCandidateGuests(r.data?.rsvps || [], { hostNames });
        setCoHostGuests(candidates);
        setCoHostGuestsError(null);
        setForm((f) => ({
          ...f,
          co_hosts: resolveCoHostFromGuests(f.co_hosts, candidates),
        }));
      } catch {
        if (!cancelled) {
          setCoHostGuests([]);
          setCoHostGuestsError("Couldn’t load guests. Check your connection, then try again.");
        }
      } finally {
        if (!cancelled) setLoadingCoHostGuests(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    isEdit,
    eventId,
    account,
    loadError,
    sourceEventId,
    sourceOrganizerName,
    coHostGuestsReloadToken,
  ]);

  function clearFieldError(key) {
    if (!key) return;
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function clearFieldErrors(keys) {
    setFieldErrors((prev) => {
      const toClear = keys.filter((k) => prev[k]);
      if (toClear.length === 0) return prev;
      const next = { ...prev };
      for (const k of toClear) delete next[k];
      return next;
    });
  }

  function update(k, v) {
    clearFieldError(k === "self_payment" ? "self_payment" : k);
    if (k === "self_payment") {
      clearFieldErrors(["self_payment_phone", "self_payment_email"]);
    }
    if (k === "early_bird_cutoff" || k === "early_bird_max_rsvps") clearFieldError("early_bird_rules");
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "pricing_type" && pricingForcesInviteOnly(v)) {
        next.access = "invite-only";
      }
      return next;
    });
  }
  function updateCity(v) {
    clearFieldError("city");
    setForm((f) => {
      if (f.city === v) return f;
      return { ...f, city: v, city_location: null, location: "", latitude: null, longitude: null };
    });
  }
  const updateCityLocation = useCallback((city) => {
    setForm((f) => ({ ...f, city_location: city }));
  }, []);
  function updateCoHostSelection(dancerId) {
    if (!dancerId) {
      setForm((f) => ({ ...f, co_hosts: [] }));
      return;
    }
    const guest = coHostGuests.find((g) => g.dancer_id === dancerId);
    if (!guest) {
      setForm((f) => ({ ...f, co_hosts: [] }));
      return;
    }
    setForm((f) => ({
      ...f,
      co_hosts: [{ dancer_id: guest.dancer_id, name: guest.name || "" }],
    }));
  }

  function retryCoHostGuests() {
    setCoHostGuestsReloadToken((n) => n + 1);
  }

  async function syncCoHostDashboardAccess(selectedDancerId) {
    if (!isEdit || !eventId || !selectedDancerId) return;
    try {
      await promoteEventCoAdmin(eventId, { dancerId: selectedDancerId });
    } catch (err) {
      const status = err?.response?.status;
      // Already co-admin is fine; other failures leave display co-host saved.
      if (status === 409) return;
      toast.error(
        formatApiError(err)
          || "Saved the co-host name, but they still can’t open the guest list. They need an account with the same phone as their RSVP.",
      );
    }
  }

  function buildDraftPayload() {
    const payload = { lifecycle: "draft" };
    if (form.name) payload.name = form.name;
    if (form.description) payload.description = form.description;
    if (form.city) payload.city = form.city;
    if (form.city_location) payload.city_location = form.city_location;
    if (form.location) payload.location = form.location;
    if (form.location_map_link) payload.location_map_link = form.location_map_link;
    if (form.starts_at) payload.starts_at = new Date(form.starts_at).toISOString();
    if (form.door_time) payload.door_time = form.door_time;
    if (form.recurring && form.recurring !== "none") payload.recurring = form.recurring;
    if (form.recurring_end) payload.recurring_end = new Date(form.recurring_end).toISOString();
    payload.pricing = {
      type: form.pricing_type,
      amount: form.pricing_amount ? parseFloat(form.pricing_amount) : null,
      currency: (form.pricing_currency || "EUR").trim().toUpperCase().slice(0, 12) || "EUR",
      early_bird_amount:
        form.early_bird_enabled && form.early_bird_amount
          ? parseFloat(form.early_bird_amount)
          : null,
      early_bird_cutoff:
        form.early_bird_enabled && form.early_bird_cutoff
          ? new Date(form.early_bird_cutoff).toISOString()
          : null,
      early_bird_max_rsvps:
        form.early_bird_enabled && form.early_bird_max_rsvps
          ? parseInt(form.early_bird_max_rsvps, 10)
          : null,
    };
    const igUrl = normalizeInstagramUrl(form.instagram_url);
    if (igUrl) payload.instagram_url = igUrl;
    payload.access = pricingForcesInviteOnly(form.pricing_type) ? "invite-only" : form.access;
    if (form.capacity) payload.capacity = parseInt(form.capacity, 10);
    if (form.leader_cap) payload.leader_cap = parseInt(form.leader_cap, 10);
    if (form.follower_cap) payload.follower_cap = parseInt(form.follower_cap, 10);
    if (form.organizer_name) payload.organizer_name = form.organizer_name;
    payload.co_hosts = coHostsPayloadFromForm(form, coHostGuests);
    payload.cover_color = form.cover_color;
    if (form.latitude != null) payload.latitude = form.latitude;
    if (form.longitude != null) payload.longitude = form.longitude;
    Object.assign(payload, buildHostSchoolPayload(form));
    if (form.pricing_type === "paid-self-managed") {
      const sp = form.self_payment || {};
      payload.self_payment = {
        use_profile_defaults: sp.use_profile_defaults !== false,
        payment_link: sp.payment_link || null,
        payment_upi: sp.payment_upi || null,
        instructions: sp.instructions || "",
        contact_phone: sp.contact_phone || null,
        contact_email: sp.contact_email || null,
        contact_telegram: sp.contact_telegram || null,
      };
    }
    return payload;
  }

  function buildPayload() {
    const payload = {
      name: form.name,
      description: form.description,
      city: form.city || null,
      city_location: form.city_location,
      location: form.location,
      location_map_link: form.location_map_link || null,
      starts_at: new Date(form.starts_at).toISOString(),
      door_time: form.door_time || null,
      recurring: form.recurring === "none" ? null : form.recurring,
      recurring_end: form.recurring_end ? new Date(form.recurring_end).toISOString() : null,
      pricing: {
        type: form.pricing_type,
        amount: form.pricing_amount ? parseFloat(form.pricing_amount) : null,
        currency: (form.pricing_currency || "EUR").trim().toUpperCase().slice(0, 12) || "EUR",
        early_bird_amount:
          form.early_bird_enabled && form.early_bird_amount
            ? parseFloat(form.early_bird_amount)
            : null,
        early_bird_cutoff:
          form.early_bird_enabled && form.early_bird_cutoff
            ? new Date(form.early_bird_cutoff).toISOString()
            : null,
        early_bird_max_rsvps:
          form.early_bird_enabled && form.early_bird_max_rsvps
            ? parseInt(form.early_bird_max_rsvps, 10)
            : null,
      },
      instagram_url: normalizeInstagramUrl(form.instagram_url),
      access: pricingForcesInviteOnly(form.pricing_type) ? "invite-only" : form.access,
      capacity: form.capacity ? parseInt(form.capacity) : null,
      leader_cap: form.leader_cap ? parseInt(form.leader_cap) : null,
      follower_cap: form.follower_cap ? parseInt(form.follower_cap) : null,
      organizer_name: form.organizer_name,
      co_hosts: coHostsPayloadFromForm(form, coHostGuests),
      cover_color: form.cover_color,
    };
    Object.assign(payload, buildHostSchoolPayload(form));
    if (form.pricing_type === "paid-self-managed") {
      const sp = form.self_payment || {};
      payload.self_payment = {
        use_profile_defaults: sp.use_profile_defaults !== false,
        payment_link: sp.payment_link || null,
        payment_upi: sp.payment_upi || null,
        instructions: sp.instructions || "",
        contact_phone: sp.contact_phone || null,
        contact_email: sp.contact_email || null,
        contact_telegram: sp.contact_telegram || null,
      };
    }
    if (form.latitude != null) payload.latitude = form.latitude;
    if (form.longitude != null) payload.longitude = form.longitude;
    return payload;
  }

  async function publishEvent(payload) {
    setLoading(true);
    try {
      const selectedCoHostId = form.co_hosts?.[0]?.dancer_id || "";
      if (isEdit) {
        const r = await api.patch(`/events/${eventId}`, payload);
        if (r.data.organizer_token) setOrgToken(r.data.id, r.data.organizer_token);
        setSourceEvent(r.data);
        await syncCoHostDashboardAccess(selectedCoHostId);
        const publishingDraft = isDraft && payload.lifecycle === "published";
        if (publishingDraft) {
          setCreated(r.data);
          toast.success("Published! Share your invite link.");
        } else if (!isDraft) {
          setPendingNotifyEvent(r.data);
          setNotifyAfterSaveOpen(true);
          toast.success("Saved — your invite link is unchanged.");
        } else {
          setCreated(r.data);
          toast.success("Saved — your invite link is unchanged.");
        }
      } else {
        const r = await api.post("/events", payload);
        setOrgToken(r.data.id, r.data.organizer_token);
        setCreated(r.data);
        toast.success("Event created! Share your invite link.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function saveDraft({ skipEditNav = false } = {}) {
    if (!account) {
      toast.error("Sign in to save a draft.");
      nav("/login?next=/create");
      return null;
    }
    setSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      const selectedCoHostId = form.co_hosts?.[0]?.dancer_id || "";
      if (isEdit || isDraft) {
        const id = eventId || sourceEvent?.id;
        await api.patch(`/events/${id}`, payload);
        if (isEdit) await syncCoHostDashboardAccess(selectedCoHostId);
        toast.success("Draft saved.");
        return { eventId: id };
      }
      const r = await api.post("/events", payload);
      toast.success("Draft saved.");
      if (!skipEditNav) {
        nav(`/e/${r.data.id}/edit`, { replace: true });
      }
      return { eventId: r.data.id };
    } catch (err) {
      toast.error(formatApiError(err));
      return null;
    } finally {
      setSavingDraft(false);
    }
  }

  function formNeedsLeaveConfirm() {
    return createEventFormNeedsLeaveConfirm(form, { isEdit, isDraft });
  }

  /** App/browser back: show leave confirm; default action is discard. */
  function requestLeaveBack(to) {
    if (!formNeedsLeaveConfirm()) {
      nav(to);
      return;
    }
    setPendingLeaveTo(to);
  }

  /** In-app link: save draft silently, then navigate — no confirm modal. */
  async function leaveViaLink(to) {
    if (!formNeedsLeaveConfirm()) {
      nav(to);
      return;
    }
    const result = await saveDraft({ skipEditNav: true });
    if (!result) return;
    // Declarative redirect — useNavigate() after an await can be dropped.
    setLeaveRedirect(to);
  }

  async function confirmLeaveTo({ save }) {
    const to = pendingLeaveTo;
    if (!to) return;
    if (save) {
      const result = await saveDraft({ skipEditNav: true });
      if (!result) {
        setPendingLeaveTo(null);
        return;
      }
    }
    setPendingLeaveTo(null);
    // Declarative redirect — useNavigate() after an await can be dropped.
    setLeaveRedirect(to);
  }

  const backTo = isEdit
    ? isDraft
      ? "/me?tab=host&section=events"
      : eventPublicPath(sourceEvent || created) || "/events"
    : fromOnboarding
      ? "/onboarding?step=loop"
      : account
        ? "/events"
        : "/";

  async function submit(e) {
    e?.preventDefault();
    const errors = validateCreateEventForm(form, {
      account,
      isDraft,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToFirstFieldError(errors));
      });
      return;
    }
    setFieldErrors({});

    const payload = buildPayload();
    if (isDraft) {
      payload.lifecycle = "published";
    }
    if (!account) {
      toast.error("Sign in to publish your event.");
      nav("/login?next=/create");
      return;
    }
    await publishEvent(payload);
  }

  async function onAuthSuccess() {
    setAuthOpen(false);
    if (pendingPayload) {
      const p = pendingPayload;
      setPendingPayload(null);
      await publishEvent(p);
    }
  }

  if (leaveRedirect) {
    return <Navigate to={leaveRedirect} replace />;
  }

  if (checking || (isEdit && loadingEvent) || (!isEdit && !checking && !account)) {
    return (
      <CreateEventLoadingSkeleton label={isEdit ? "Loading event…" : "Loading…"} />
    );
  }

  if (isEdit && loadError === "forbidden") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-8 text-center">
        <ReadableCard className="p-7 max-w-md">
          <p className="font-ui font-semibold text-[20px]">Not your event</p>
          <p className="font-ui font-medium mt-2 text-[14px] text-ink/80 leading-relaxed">Sign in as the host to edit this event.</p>
          <Link to={eventPublicPath(sourceEvent) || "/events"} className="font-ui underline font-semibold mt-3 inline-block text-[14px]">← Back to event</Link>
        </ReadableCard>
      </div>
    );
  }

  if (isEdit && loadError === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-8 text-center">
        <p className="font-ui font-semibold text-[20px]">Event not found.</p>
        <Link to="/events" className="font-ui underline font-semibold mt-3 inline-block text-[14px]">← My events</Link>
      </div>
    );
  }

  if (created) {
    return <CreateEventSuccessScreen event={created} isEdit={isEdit} onboardingReturn={fromOnboarding} />;
  }

  function finishNotifyPrompt(proceedToBroadcast) {
    setNotifyAfterSaveOpen(false);
    if (proceedToBroadcast && pendingNotifyEvent) {
      nav(`/dashboard/${pendingNotifyEvent.id}?broadcast=1`);
      setPendingNotifyEvent(null);
      return;
    }
    if (pendingNotifyEvent) {
      setCreated(pendingNotifyEvent);
      setPendingNotifyEvent(null);
    }
  }

  return (
    <>
      <NotifyAfterSaveDialog
        open={notifyAfterSaveOpen}
        onYes={() => finishNotifyPrompt(true)}
        onNo={() => finishNotifyPrompt(false)}
      />
    <div className="min-h-screen bg-canvas font-body text-ink pb-24 sm:pb-8">
      <div className="max-w-page mx-auto px-4 pt-6">
        <ReadableNavButton
          as={Link}
          to={backTo}
          onClick={(e) => {
            if (!formNeedsLeaveConfirm()) return;
            e.preventDefault();
            requestLeaveBack(backTo);
          }}
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} aria-hidden />{" "}
          {isEdit
            ? isDraft
              ? "Drafts"
              : "Event page"
            : fromOnboarding
              ? "Setup"
              : account
                ? "My events"
                : "Home"}
        </ReadableNavButton>
      </div>

      <main className="max-w-page mx-auto px-4 mt-6">
        <h1 className="font-ui font-semibold text-[22px] sm:text-[24px] leading-tight text-balance">
          {isDraft ? "Finish draft" : isEdit ? "Edit event" : "Create event"}
        </h1>
        <FieldHint className="mt-1.5 text-[14px]">
          {isDraft
            ? "Complete the form, then publish when ready."
            : isEdit && !isDraft
            ? "Your invite link stays the same after you save."
            : "Fill in the details — you'll get a shareable link when you publish."}
        </FieldHint>

        <form
          id={CREATE_EVENT_FORM_ID}
          onSubmit={submit}
          className="mt-8 space-y-6"
          noValidate
          aria-busy={loading || savingDraft}
        >
          <FormValidationAlert count={Object.keys(fieldErrors).length} />
        {/* Basics */}
        <ReadableCard className={FORM_CARD_CLASS}>
          <FormTextField
            id={CREATE_EVENT_FIELD_IDS.name}
            label="Event name"
            required
            error={fieldErrors.name}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Salsa Social Friday 💃"
            data-testid="event-name-input"
          />
          <FormTextField
            id="create-event-description"
            label="Description"
            as="textarea"
            className="min-h-[88px]"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Cuban + LA salsa all night. Beginners welcome!"
            data-testid="description-input"
          />
          <FormTextField
            id={CREATE_EVENT_FIELD_IDS.organizer_name}
            label="Host name"
            required
            error={fieldErrors.organizer_name}
            value={form.organizer_name}
            onChange={(e) => update("organizer_name", e.target.value)}
            placeholder="Marta"
            data-testid="organizer-input"
          />
        </ReadableCard>

        {account && (
          <ReadableCard className={FORM_CARD_CLASS} data-testid="host-school-section">
            <HostSchoolPromotionField
              value={{
                school_id: form.school_id,
                school_external_name: form.school_external_name,
                school_external_url: form.school_external_url,
                host_school_opt_out: form.host_school_opt_out,
              }}
              error={fieldErrors.host_school}
              onChange={(next) => {
                setForm((f) => ({
                  ...f,
                  school_id: next.school_id,
                  school_external_name: next.school_external_name,
                  school_external_url: next.school_external_url,
                  host_school_opt_out: next.host_school_opt_out,
                }));
                clearFieldError("host_school");
              }}
            />
          </ReadableCard>
        )}

        {isEdit && (
          <ReadableCard className={FORM_CARD_CLASS}>
            <SectionTitle>Co-host (optional)</SectionTitle>
            {loadingCoHostGuests ? (
              <div className="space-y-2" aria-busy="true" data-testid="co-host-loading">
                <div className="h-4 w-40 rounded-md2 bg-black/[0.06] animate-pulse motion-reduce:animate-none" />
                <div className="h-11 w-full rounded-md2 bg-black/[0.06] animate-pulse motion-reduce:animate-none" />
                <p className="sr-only">Loading guests</p>
              </div>
            ) : coHostGuestsError ? (
              <div className="space-y-3" data-testid="co-host-load-error" role="alert">
                <FieldHint id="create-event-co-host-error-hint">{coHostGuestsError}</FieldHint>
                <ReadableNavButton
                  type="button"
                  onClick={retryCoHostGuests}
                  className="min-h-[44px] px-3 text-[13px]"
                  data-testid="co-host-retry-btn"
                >
                  Try again
                </ReadableNavButton>
              </div>
            ) : coHostGuests.length === 0 ? (
              <FieldHint id="create-event-co-host-empty-hint" data-testid="co-host-empty-hint">
                No RSVPs yet. Ask your co-host to RSVP (Going or Maybe), then choose them here.
              </FieldHint>
            ) : (
              <div>
                <FormLabel as="label" htmlFor={CREATE_EVENT_CO_HOST_FIELD_ID} className="block mb-2">
                  Co-host
                </FormLabel>
                <select
                  id={CREATE_EVENT_CO_HOST_FIELD_ID}
                  className="field"
                  value={selectedCoHostId}
                  onChange={(e) => updateCoHostSelection(e.target.value)}
                  aria-describedby={
                    selectedCoHostIsMaybe
                      ? "create-event-co-host-hint create-event-co-host-maybe-hint"
                      : "create-event-co-host-hint"
                  }
                  data-testid="co-host-select"
                >
                  <option value="">None — just me</option>
                  {coHostGuests.map((g) => (
                    <option key={g.dancer_id} value={g.dancer_id} title={g.name || "Guest"}>
                      {coHostOptionLabel(g)}
                    </option>
                  ))}
                </select>
                <FieldHint id="create-event-co-host-hint" className="mt-2">
                  They can open the guest list and manage this event with you. Applies when you save. One person only.
                </FieldHint>
                {selectedCoHostIsMaybe && (
                  <FieldHint
                    id="create-event-co-host-maybe-hint"
                    className="mt-1.5"
                    data-testid="co-host-maybe-hint"
                  >
                    They’re only Maybe — pick them only if you trust them with the guest list.
                  </FieldHint>
                )}
              </div>
            )}
          </ReadableCard>
        )}

        {/* Where */}
        <ReadableCard className={FORM_CARD_CLASS}>
          <SectionTitle icon={MapPin}>Where</SectionTitle>
          <div className="block">
            <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.city} className="block mb-2">City *</FormLabel>
            <CityAutocomplete
              inputId={CREATE_EVENT_FIELD_IDS.city}
              error={fieldErrors.city}
              value={form.city}
              onChange={updateCity}
              onResolvedChange={updateCityLocation}
              placeholder={locale.default_city || "Berlin"}
              testId="city-input"
              required
              className="field"
            />
          </div>
          <VenueInput
            mode="event"
            location={form.location}
            onLocationChange={(v) => {
              clearFieldError("location");
              update("location", v);
            }}
            locationMapLink={form.location_map_link}
            onLocationMapLinkChange={(v) => update("location_map_link", v)}
            cityHint={form.city}
            locationError={fieldErrors.location}
            locationInputId={CREATE_EVENT_FIELD_IDS.location}
            mapLinkInputId={CREATE_EVENT_FIELD_IDS.map_link}
            required
          />
          <div className="block border-t border-black/[0.08] pt-4">
            <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.instagram_url} className="block mb-2">Venue Instagram (optional)</FormLabel>
            <InstagramInput
              id={CREATE_EVENT_FIELD_IDS.instagram_url}
              value={form.instagram_url}
              onChange={(v) => update("instagram_url", v)}
              placeholder="your-venue"
              data-testid="event-instagram-input"
            />
            <FieldHint className="mt-1.5 text-[12px] sm:text-[13px]">
              For the venue or event page — separate from your host profile Instagram.
            </FieldHint>
          </div>
        </ReadableCard>

        {/* When */}
        <ReadableCard className={FORM_CARD_CLASS}>
          <SectionTitle icon={Clock}>When</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="block">
              <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.starts_at} className="block mb-2">Date & start time *</FormLabel>
              <input
                id={CREATE_EVENT_FIELD_IDS.starts_at}
                type="datetime-local"
                className={fieldClass(fieldErrors.starts_at)}
                value={form.starts_at}
                onChange={(e) => update("starts_at", e.target.value)}
                required
                aria-invalid={Boolean(fieldErrors.starts_at)}
                aria-describedby={fieldErrors.starts_at ? `${CREATE_EVENT_FIELD_IDS.starts_at}-error` : undefined}
                data-testid="starts-at-input"
              />
              {fieldErrors.starts_at ? (
                <FormFieldError id={`${CREATE_EVENT_FIELD_IDS.starts_at}-error`}>{fieldErrors.starts_at}</FormFieldError>
              ) : null}
            </div>
            <div className="block">
              <FormLabel as="label" htmlFor="create-event-door-time" className="block mb-2">Doors open at (optional)</FormLabel>
              <input
                id="create-event-door-time"
                type="time"
                className="field"
                value={form.door_time}
                onChange={(e) => update("door_time", e.target.value)}
                placeholder="21:00"
                data-testid="door-time-input"
              />
            </div>
          </div>

          <div>
            <FormLabel id={CREATE_EVENT_FIELD_IDS.recurring} as="span" className="block mb-2 flex items-center gap-2">
              <Repeat className="w-3.5 h-3.5" strokeWidth={2} aria-hidden /> Repeats?
            </FormLabel>
            <div role="radiogroup" aria-labelledby={CREATE_EVENT_FIELD_IDS.recurring}>
              <ChoiceToggleGroup
                role="radio"
                testIdPrefix="recurring"
                value={form.recurring}
                onChange={(k) => update("recurring", k)}
                options={[
                  { k: "none", label: "One-off" },
                  { k: "weekly", label: "Weekly" },
                  { k: "biweekly", label: "Bi-weekly" },
                  { k: "monthly", label: "Monthly" },
                ]}
              />
            </div>
          </div>
          {form.recurring !== "none" && (
            <div className="block">
              <FormLabel as="label" htmlFor="create-event-recurring-end" className="block mb-2">Series ends (optional)</FormLabel>
              <input
                id="create-event-recurring-end"
                type="datetime-local"
                className="field"
                value={form.recurring_end}
                onChange={(e) => update("recurring_end", e.target.value)}
                placeholder="leave empty for ongoing"
                data-testid="recurring-end-input"
              />
              <FieldHint className="mt-1.5">
                Leave empty for an ongoing series. Each occurrence has its own RSVP list.
              </FieldHint>
            </div>
          )}
        </ReadableCard>

        {/* Pricing */}
        <ReadableCard className={FORM_CARD_CLASS}>
          <SectionTitle>Pricing</SectionTitle>
          <ChoiceToggleGroup
            value={form.pricing_type}
            onChange={(k) => update("pricing_type", k)}
            testIdPrefix="pricing"
            options={[
              { k: "free", label: "Free" },
              { k: "paid-at-venue", label: "Pay at door" },
              { k: "paid-self-managed", label: "You collect payment" },
            ]}
          />
          <FieldHint className="text-[12px] sm:text-[13px]">
            Pay at door: guests pay on arrival. You collect payment: guests pay you online before you approve them.
          </FieldHint>
          {(form.pricing_type === "paid-at-venue" || form.pricing_type === "paid-self-managed") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="block">
                <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.pricing_amount} className="block mb-2">Ticket price *</FormLabel>
                <input
                  id={CREATE_EVENT_FIELD_IDS.pricing_amount}
                  type="number"
                  min="0"
                  step="0.01"
                  className={fieldClass(fieldErrors.pricing_amount)}
                  value={form.pricing_amount}
                  onChange={(e) => update("pricing_amount", e.target.value)}
                  placeholder="12"
                  aria-invalid={Boolean(fieldErrors.pricing_amount)}
                  aria-describedby={fieldErrors.pricing_amount ? `${CREATE_EVENT_FIELD_IDS.pricing_amount}-error` : undefined}
                  data-testid="pricing-amount-input"
                />
                {fieldErrors.pricing_amount ? (
                  <FormFieldError id={`${CREATE_EVENT_FIELD_IDS.pricing_amount}-error`}>{fieldErrors.pricing_amount}</FormFieldError>
                ) : null}
              </div>
              <div className="block">
                <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.pricing_currency} className="block mb-2">Currency</FormLabel>
                <input
                  id={CREATE_EVENT_FIELD_IDS.pricing_currency}
                  type="text"
                  className="field"
                  value={form.pricing_currency}
                  onChange={(e) => update("pricing_currency", e.target.value)}
                  placeholder={locale.currency || "EUR"}
                  maxLength={12}
                  autoComplete="off"
                  data-testid="pricing-currency-input"
                />
              </div>
            </div>
          )}
          {(form.pricing_type === "paid-at-venue" || form.pricing_type === "paid-self-managed") && (
            <div className="border-t border-black/[0.08] pt-4 space-y-3" data-testid="early-bird-section">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.early_bird_enabled}
                  onChange={(e) => update("early_bird_enabled", e.target.checked)}
                  className="w-5 h-5 accent-plum mt-0.5 shrink-0"
                  data-testid="early-bird-toggle"
                />
                <div>
                  <FormLabel as="span" className="block">Early bird discount</FormLabel>
                  <FieldHint className="mt-0.5">
                    Set a deadline, a spot limit, or both — early bird ends when the first limit is reached.
                  </FieldHint>
                </div>
              </label>
              {form.early_bird_enabled && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="block">
                      <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.early_bird_amount} className="block mb-2">Early bird price *</FormLabel>
                      <input
                        id={CREATE_EVENT_FIELD_IDS.early_bird_amount}
                        type="number"
                        min="0"
                        step="0.01"
                        className={fieldClass(fieldErrors.early_bird_amount)}
                        value={form.early_bird_amount}
                        onChange={(e) => update("early_bird_amount", e.target.value)}
                        placeholder="8"
                        aria-invalid={Boolean(fieldErrors.early_bird_amount)}
                        aria-describedby={fieldErrors.early_bird_amount ? `${CREATE_EVENT_FIELD_IDS.early_bird_amount}-error` : undefined}
                        data-testid="early-bird-amount-input"
                      />
                      {fieldErrors.early_bird_amount ? (
                        <FormFieldError id={`${CREATE_EVENT_FIELD_IDS.early_bird_amount}-error`}>{fieldErrors.early_bird_amount}</FormFieldError>
                      ) : null}
                    </div>
                    <div className="block">
                      <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.early_bird_cutoff} className="block mb-2">Valid until</FormLabel>
                      <input
                        id={CREATE_EVENT_FIELD_IDS.early_bird_cutoff}
                        type="datetime-local"
                        className={fieldClass(fieldErrors.early_bird_rules)}
                        value={form.early_bird_cutoff}
                        onChange={(e) => update("early_bird_cutoff", e.target.value)}
                        aria-invalid={Boolean(fieldErrors.early_bird_rules)}
                        aria-describedby={fieldErrors.early_bird_rules ? `${CREATE_EVENT_FIELD_IDS.early_bird_rules}-error` : undefined}
                        data-testid="early-bird-cutoff-input"
                      />
                    </div>
                    <div className="block">
                      <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.early_bird_max_rsvps} className="block mb-2">Max early-bird spots</FormLabel>
                      <input
                        id={CREATE_EVENT_FIELD_IDS.early_bird_max_rsvps}
                        type="number"
                        min="1"
                        step="1"
                        className={fieldClass(fieldErrors.early_bird_max_rsvps)}
                        value={form.early_bird_max_rsvps}
                        onChange={(e) => update("early_bird_max_rsvps", e.target.value)}
                        placeholder="20"
                        aria-invalid={Boolean(fieldErrors.early_bird_max_rsvps)}
                        aria-describedby={
                          fieldErrors.early_bird_max_rsvps
                            ? `${CREATE_EVENT_FIELD_IDS.early_bird_max_rsvps}-error`
                            : fieldErrors.early_bird_rules
                              ? `${CREATE_EVENT_FIELD_IDS.early_bird_rules}-error`
                              : undefined
                        }
                        data-testid="early-bird-max-rsvps-input"
                      />
                      {fieldErrors.early_bird_max_rsvps ? (
                        <FormFieldError id={`${CREATE_EVENT_FIELD_IDS.early_bird_max_rsvps}-error`}>
                          {fieldErrors.early_bird_max_rsvps}
                        </FormFieldError>
                      ) : null}
                    </div>
                  </div>
                  {fieldErrors.early_bird_rules ? (
                    <FormFieldError id={`${CREATE_EVENT_FIELD_IDS.early_bird_rules}-error`}>{fieldErrors.early_bird_rules}</FormFieldError>
                  ) : null}
                  {earlyBirdRecurringHint ? (
                    <FieldHint>{earlyBirdRecurringHint}</FieldHint>
                  ) : null}
                  <FieldHint>
                    Preview:{" "}
                    <span className="font-ui font-semibold text-[13px] text-ink" data-testid="early-bird-preview-pill">
                      {pricingPillLabel(previewPricing)}
                    </span>
                  </FieldHint>
                </>
              )}
            </div>
          )}
          {form.pricing_type === "paid-self-managed" && (
            <SelfPaymentForm
              values={form.self_payment}
              account={account}
              onChange={(sp) => {
                if (sp.use_profile_defaults !== false && account) {
                  update("self_payment", { use_profile_defaults: true, ...emptyPaymentFields(account) });
                  return;
                }
                update("self_payment", sp);
              }}
              pricing={previewPaymentPricing}
              payeeName={form.organizer_name}
              showProfileToggle={!!account}
              idPrefix="event-payment"
              error={fieldErrors.self_payment}
              fieldErrors={{
                contact_phone: fieldErrors.self_payment_phone,
                contact_email: fieldErrors.self_payment_email,
              }}
              onHostProfileClick={() => leaveViaLink("/me?tab=host&section=payment")}
            />
          )}
        </ReadableCard>

        {/* Access & capacity */}
        <ReadableCard className={FORM_CARD_CLASS}>
          <SectionTitle>Access & capacity</SectionTitle>
          {form.pricing_type === "paid-self-managed" && (
            <div id={CREATE_EVENT_FIELD_IDS.access_paid_note} className="border-t border-black/[0.08] pt-3">
              <FieldHint className="text-[14px] text-ink">
                You approve guests after they pay — open RSVPs aren&apos;t available when you collect payment.
              </FieldHint>
            </div>
          )}
          <ChoiceToggleGroup
            value={form.access}
            onChange={(k) => update("access", k)}
            testIdPrefix="access"
            isDisabled={(k) => pricingForcesInviteOnly(form.pricing_type) && k === "open"}
            getDescribedBy={(k) =>
              pricingForcesInviteOnly(form.pricing_type) && k === "open"
                ? CREATE_EVENT_FIELD_IDS.access_paid_note
                : undefined
            }
            options={[
              { k: "open", label: "Anyone with link" },
              { k: "invite-only", label: "Invite only" },
            ]}
          />
          <FieldHint className="text-[12px] sm:text-[13px]">
            Leave capacity blank for unlimited. Leader and follower caps balance the floor.
          </FieldHint>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="block">
              <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.capacity} className="block mb-2">Max guests</FormLabel>
              <input
                id={CREATE_EVENT_FIELD_IDS.capacity}
                type="number"
                min="0"
                className="field"
                value={form.capacity}
                onChange={(e) => update("capacity", e.target.value)}
                placeholder="80"
                data-testid="capacity-input"
              />
            </div>
            <div className="block">
              <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.leader_cap} className="block mb-2">Max leaders</FormLabel>
              <input
                id={CREATE_EVENT_FIELD_IDS.leader_cap}
                type="number"
                min="0"
                className="field"
                value={form.leader_cap}
                onChange={(e) => update("leader_cap", e.target.value)}
                placeholder="40"
              />
            </div>
            <div className="block">
              <FormLabel as="label" htmlFor={CREATE_EVENT_FIELD_IDS.follower_cap} className="block mb-2">Max followers</FormLabel>
              <input
                id={CREATE_EVENT_FIELD_IDS.follower_cap}
                type="number"
                min="0"
                className="field"
                value={form.follower_cap}
                onChange={(e) => update("follower_cap", e.target.value)}
                placeholder="40"
              />
            </div>
          </div>
        </ReadableCard>

        {/* Style */}
        <ReadableCard className={FORM_STYLE_CARD_CLASS}>
          <SectionTitle>Cover colour</SectionTitle>
          <CoverColorPicker
            value={form.cover_color}
            onChange={(id) => update("cover_color", id)}
          />
        </ReadableCard>

        <div className="hidden sm:flex flex-wrap justify-end gap-2 pt-6 border-t border-black/[0.08]">
          <ReadableNavButton
            type="button"
            disabled={loading || savingDraft}
            onClick={() => saveDraft()}
            data-testid="save-draft-btn"
          >
            {savingDraft ? "Saving…" : "Save draft"}
          </ReadableNavButton>
          <ReadableNavButton
            type="submit"
            variant="primary"
            disabled={loading || savingDraft}
            data-testid="submit-event-btn"
          >
            {getCreateEventPublishLabel({ loading, isDraft, isEdit, account })}
          </ReadableNavButton>
        </div>
      </form>
      </main>

      <CreateEventStickyBar
        formId={CREATE_EVENT_FORM_ID}
        savingDraft={savingDraft}
        loading={loading}
        publishLabel={getCreateEventPublishLabel(
          { loading, isDraft, isEdit, account },
          { compact: true }
        )}
        onSaveDraft={() => saveDraft()}
      />

      <ConfirmLeaveModal
        open={Boolean(pendingLeaveTo)}
        busy={savingDraft}
        onCancel={() => setPendingLeaveTo(null)}
        onConfirm={confirmLeaveTo}
        title="Leave without saving?"
        body="You have unsaved details on this form. Discard them, or save a draft before you leave."
        checkboxLabel="Save draft before you continue"
        cancelLabel="Cancel"
        saveLabel="Save draft"
        discardLabel="Discard"
        testId="create-leave-confirm-modal"
      />

      {!isEdit && (
        <AuthModal
          open={authOpen}
          onClose={() => setAuthOpen(false)}
          onSuccess={onAuthSuccess}
          title="Sign in to publish"
          subtitle="Your event is ready. Pick Google or Apple so you can manage it from any device."
        />
      )}
    </div>
    </>
  );
}
